import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class I18nService {
  private translations: Record<string, string> = {};
  private currentLang = 'en';

  public constructor(private http: HttpClient) {
    this.initLanguage();
  }

  /**
   * Initialize language from browser or default to English
   */
  private initLanguage(): void {
    const browserLang = this.getBrowserLanguage();
    const lang = this.getSupportedLanguage(browserLang);
    this.loadTranslations(lang);
  }

  /**
   * Get browser language
   */
  private getBrowserLanguage(): string {
    return navigator.language.split('-')[0]; // Get 'en' from 'en-US'
  }

  /**
   * Check if language is supported, fallback to English
   */
  private getSupportedLanguage(lang: string): string {
    const supportedLanguages = ['en', 'fr'];
    return supportedLanguages.includes(lang) ? lang : 'en';
  }

  /**
   * Load translation file for given language
   */
  public loadTranslations(lang: string): Observable<Record<string, string>> {
    return this.http.get<Record<string, string>>(`/assets/i18n/${lang}.json`).pipe(
      tap((translations) => {
        this.translations = translations;
        this.currentLang = lang;
      }),
      catchError((error) => {
        console.error(`Failed to load translations for ${lang}:`, error);
        // Fallback to English if translation file not found
        if (lang !== 'en') {
          return this.loadTranslations('en');
        }
        return of({});
      })
    );
  }

  /**
   * Get translation for a key using dot notation
   * Supports both flat and nested JSON structures
   */
  public translate(key: string, params?: Record<string, string | number>): string {
    const translations = this.translations;
    let translation = this.getNestedValue(translations, key) ?? key;

    // Replace parameters in translation
    if (params) {
      Object.keys(params).forEach((param) => {
        translation = translation.replace(`{{${param}}}`, String(params[param]));
      });
    }

    return translation;
  }

  /**
   * Get nested value from object using dot notation
   * e.g., 'auth.signIn.title' returns translations.auth.signIn.title
   */
  private getNestedValue(obj: Record<string, unknown>, key: string): string | undefined {
    return key.split('.').reduce<unknown>((current, part) => {
      if (
        current !== null &&
        current !== undefined &&
        typeof current === 'object' &&
        part in current
      ) {
        return (current as Record<string, unknown>)[part];
      }
      return undefined;
    }, obj) as string | undefined;
  }

  /**
   * Get current language
   */
  public getCurrentLanguage(): string {
    return this.currentLang;
  }

  /**
   * Change language
   */
  public changeLanguage(lang: string): void {
    this.loadTranslations(lang).subscribe();
  }
}
